import * as React from 'react';
import * as mobx from 'mobx';
import * as mobxReact from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { AudioBridgeProvider, ConnectionType } from '../../../../../../api/connections.dao';
import { CommonModal } from '../../../../../global/modal/common/common-modal';
import { DefaultCloseModalButton } from '../../../../../global/modal/common/common-close-modal-buttons';
import { FormField } from '../../../../../global/form/form-field/form-field';
import { DefaultDatePicker } from '../../../../../global/default-datepicker/default-datepicker';
import { SelectTimezones } from '../../../../../global/select-timezone/select-timezones';
import DefaultButton from '../../../../../global/default-button/default-button';
import './create-audio-bridge.scss';
import { CreateAudioBridgeVM } from './create-audio-bridge.viewmodel';
import { SPACE_IN_BEGIN_REG_EXP } from '../../../../../../constants/regex.constants';
import { AUDIO_BRIDGE_NAME, CONFERENCE_MODE, END_DATE, PROVIDER, START_DATE, TELEPHONE_NUMBER_SET, TIME_ZONE } from '../../../../../../constants/audio-bridge-connection.constant';
import { AudioBridgeProviderLabel } from '../../../../../../infrastructure/utils/common.utils';
import { OEToggle } from '../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../global/text/text';
import { EnablementStatus } from '../../../../../../constants/common.constants';
import { Permission } from '../../../../../../api/roles.dao';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { InfoPopover } from '../../../../../global/info-popover/info-popover';
import { syncAudioBridgeAttendeeInfoMessage } from '../../../../../../constants/connections-labels.constants';

export interface IPresentationProps {
    vm: CreateAudioBridgeVM;
    isMeetingConnectionsForm?: boolean;
    isAutoSyncLimitReached?: boolean;
}

const CreateAudioBridgeModal = mobxReact.observer(({
    vm,
    isAutoSyncLimitReached,
    isMeetingConnectionsForm,
}: IPresentationProps) => {
    const { intl } = vm;

    const formVM = vm;
    const formData = formVM.createAudioBridgeFormDataVM;

    const titleLabel = <FormattedMessage id='oe.audioBridge.createTitle' defaultMessage='Create Audio Bridge' />;
    const detailsBtnLabel = <FormattedMessage id='oe.audioBridge.addDetailsManually' defaultMessage='Add Details Manually' />;

    const autoSyncPermission = hasPermission(Permission.CREATE_AUTOSYNC_CONNECTION);

    const showAutoRegAndAutoSyncToggles = [
        isMeetingConnectionsForm,
        formVM.isMeetingV2Enabled,
        !isAutoSyncLimitReached,
        autoSyncPermission
    ].every(Boolean);

    const footer = <div className='buttons-block'>
        <DefaultButton
            text={detailsBtnLabel}
            modifier={['fat', 'cancel']}
            onClick={vm.openAudioBridgeConnectionForm.bind(vm, { type: ConnectionType.AudioBridge })}
        />
        <div className='buttons-block buttons-block--from-right'>
            <DefaultButton
                text={titleLabel}
                modifier={['fat', 'confirm-safe']}
                onClick={() => formVM.createAudioBridgeHandle(showAutoRegAndAutoSyncToggles)}
                disabled={formVM.getIsFormSaving()}
            />
            <DefaultCloseModalButton />
        </div>
    </div>;

    return (
        <>
            <CommonModal
                title={titleLabel}
                isOpen={formVM.getIsFormShown()}
                onRequestClose={() => {
                    formVM.hideForm();
                }}
                className='oe-modal-width create-audio-bridge-modal'
                classNameBody='body__scrollable'
                footer={footer}
            >
                <form className='oe-form'>
                    <div className='m-bottom-25px'>
                        <div className='oe-form__row'>
                            <FormField className='m-bottom-4px' label={intl.formatMessage(START_DATE)}>
                                <DefaultDatePicker
                                    selected={formData.getStartDateTime()}
                                    onChange={date => { formData.setStartDateTime(date); }}
                                    minDate={formData.getStartMinDate()}
                                    maxDate={null}
                                    placeholderText='Select start date time'
                                    className='oe-form-field__control'
                                />
                            </FormField>

                            <FormField className='m-bottom-4px' label={intl.formatMessage(END_DATE)}>
                                <DefaultDatePicker
                                    selected={formData.getEndDateTime()}
                                    onChange={date => { formData.setEndDateTime(date); }}
                                    placeholderText='Select end date time'
                                    minDate={formData.getStartDateTime() || new Date()}
                                    maxDate={null}
                                    className='oe-form-field__control'
                                />
                            </FormField>
                        </div>
                        {formData.getFieldError('dates') && <span className='oe-form-field__error'>{formData.getFieldError('dates')}</span>}
                    </div>
                    <FormField label={intl.formatMessage(TIME_ZONE)}>
                        <SelectTimezones
                            className='oe-form-field__control'
                            value={formData.getTimeZone()}
                            onChange={mobx.action(timeZone => formData.setTimeZone(timeZone))}
                        />
                    </FormField>
                    <FormField label={intl.formatMessage(PROVIDER)}>
                        <select
                            className="oe-form-field__control"
                            value={formData.getProvider()}
                            onChange={({ target: { value } }) => formData.setProvider(value.replace(SPACE_IN_BEGIN_REG_EXP, ''))}
                            disabled={true}
                        >
                            {Object.keys(AudioBridgeProvider).map((item: AudioBridgeProvider) =>
                                <option
                                    key={item}
                                    value={AudioBridgeProvider[item]}
                                >
                                    {AudioBridgeProviderLabel[item]}
                                </option>
                            )}
                        </select>
                    </FormField>

                    <FormField label={intl.formatMessage(TELEPHONE_NUMBER_SET)}>
                        <select
                            className="oe-form-field__control"
                            value={formData.audioProviderId}
                            onChange={({ target: { value } }) => formData.setAudioProviderId(value.replace(SPACE_IN_BEGIN_REG_EXP, ''))}
                        >
                            {formData.getTelephoneNumberSetOptions().map(item => {
                                return <option
                                    key={item.value}
                                    value={item.value}
                                >
                                    {item.label}
                                </option>;
                            })}
                        </select>
                    </FormField>

                    <FormField label={intl.formatMessage(AUDIO_BRIDGE_NAME)}>
                        <input
                            className='oe-form-field__control'
                            value={formData.getAudioBridgeName()}
                            onChange={e => { formData.setAudioBridgeName(e.target.value); }}
                            onBlur={() => { formData.checkAudioBridgeName(); }}
                        />
                        {formData.getFieldError('audioBridgeName') && <span className='oe-form-field__error'>{formData.getFieldError('audioBridgeName')}</span>}
                    </FormField>


                    <FormField label={intl.formatMessage(CONFERENCE_MODE)}>
                        <select
                            className="oe-form-field__control"
                            value={formData.getConferenceMode()}
                            onChange={({ target: { value } }) => formData.setConferenceMode(value.replace(SPACE_IN_BEGIN_REG_EXP, ''))}
                        >
                            {vm.getConferenceModeOptions().map((option, index) => (
                                <option key={index} value={option.value} > {option.label} </option>
                            ))}
                        </select>
                    </FormField>
                    <FormField
                        className='form-toggle m-top-10px'
                    >
                        <OEToggle
                            isSelected={formData.getAutomaticallyRecordEnabled()}
                            onClick={formData.toggleAutomaticallyRecordEnabled}
                        />
                        <Text
                            message='Automatically Record is {prefix}'
                            id='oe.audioBridge.automaticallyRecordEnabled'
                            description='Label text'
                            values={{
                                prefix: formData.getAutomaticallyRecordEnabled() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                            }}
                        />
                    </FormField>
                    {
                        showAutoRegAndAutoSyncToggles &&
                        <FormField
                            className='form-toggle m-top-10px'
                        >
                            <OEToggle
                                isSelected={formData.getAutoSyncEnabled()}
                                onClick={formData.toggleAutoSyncEnabled}
                            />
                            <Text
                                message='Sync Meeting Attendees with Turbobridge {prefix}'
                                id='oe.audioBridge.autoSyncEnabled'
                                description='Label text'
                                values={{
                                    prefix: formData.getAutoSyncEnabled() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                }}
                            />
                            <InfoPopover className='m-left-8px'>
                                <div>{syncAudioBridgeAttendeeInfoMessage}</div>
                            </InfoPopover>
                        </FormField>
                    }
                </form>
            </CommonModal>
        </>
    );
});

export { CreateAudioBridgeModal };